// src/pages/Admin/AdminApproval.js
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ImageList,
  ImageListItem,
  Divider,
  Grid
} from '@mui/material'
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Home as HomeIcon,
  PhotoLibrary as PhotoIcon,
  Close as CloseIcon,
  ArrowBackIos as PrevIcon,
  ArrowForwardIos as NextIcon,
  SquareFoot as AreaIcon,
  KingBed as BedIcon,
  Bathroom as BathIcon,
  DirectionsCar as GarageIcon
} from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import { adminAPI } from '../../services/api'
import { API_BASE_URL } from '../../config'

const AdminApproval = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [properties, setProperties] = useState([])
  const [processing, setProcessing] = useState({})

  // Estado do modal de imagens
  const [imageModal, setImageModal] = useState({ open: false, property: null, currentIndex: 0 })

  useEffect(() => {
    if (user && !user.is_staff) {
      navigate('/')
    }
  }, [user, navigate])

  useEffect(() => {
    fetchPending()
  }, [])

  const fetchPending = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await adminAPI.getPendingProperties()
      setProperties(response.data.results || response.data || [])
    } catch (err) {
      setError('Erro ao carregar imóveis pendentes.')
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (propertyId, action) => {
    setProcessing((prev) => ({ ...prev, [propertyId]: true }))
    try {
      await adminAPI.approveProperty(propertyId, action)
      setProperties((prev) => prev.filter((p) => p.id !== propertyId))
      if (imageModal.property?.id === propertyId) {
        setImageModal({ open: false, property: null, currentIndex: 0 })
      }
    } catch (err) {
      setError('Erro ao processar ação. Tente novamente.')
    } finally {
      setProcessing((prev) => ({ ...prev, [propertyId]: false }))
    }
  }

  const openImageModal = (property) => {
    setImageModal({ open: true, property, currentIndex: 0 })
  }

  const closeImageModal = () => {
    setImageModal({ open: false, property: null, currentIndex: 0 })
  }

  const getImages = (property) => {
    if (!property?.images?.length) return []
    // Ordena: imagem principal primeiro
    return [...property.images].sort((a, b) => (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0))
  }

  const getImageUrl = (img) => {
    if (!img?.image) return null
    return img.image.startsWith('http') ? img.image : `${API_BASE_URL}${img.image}`
  }

  const formatPrice = (property) => {
    if (property.price_on_request) return 'Valor a Combinar'
    if (!property.price) return '-'
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(property.price)
  }

  const getMainImageUrl = (property) => {
    const images = getImages(property)
    return images.length ? getImageUrl(images[0]) : null
  }

  const currentImages = imageModal.property ? getImages(imageModal.property) : []
  const currentImg = currentImages[imageModal.currentIndex]

  return (
    <Container maxWidth='xl' sx={{ py: 4 }}>
      <Box display='flex' alignItems='center' gap={2} mb={3}>
        <HomeIcon color='primary' sx={{ fontSize: 32 }} />
        <Typography variant='h4' fontWeight={700}>
          Aprovação de Anúncios
        </Typography>
        <Chip
          label={`${properties.length} pendente${properties.length !== 1 ? 's' : ''}`}
          color='warning'
          sx={{ fontWeight: 700 }}
        />
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display='flex' justifyContent='center' py={8}>
          <CircularProgress />
        </Box>
      ) : properties.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ApproveIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant='h6' color='text.secondary'>
            Nenhum anúncio aguardando aprovação
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>Imóvel</TableCell>
                <TableCell>Proprietário</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Preço</TableCell>
                <TableCell>Cidade</TableCell>
                <TableCell>Cadastrado em</TableCell>
                <TableCell align='center'>Fotos</TableCell>
                <TableCell align='center'>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((property) => (
                  <TableRow key={property.id} hover>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1.5}>
                        <Avatar
                          src={getMainImageUrl(property)}
                          variant='rounded'
                          sx={{ width: 56, height: 56 }}
                        >
                          <HomeIcon />
                        </Avatar>
                        <Box>
                          <Typography variant='body2' fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                            {property.title}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            #{property.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{property.owner_name || property.owner}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={property.transaction_type === 'venda' ? 'Venda' : 'Aluguel'}
                        size='small'
                        color={property.transaction_type === 'venda' ? 'success' : 'info'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' fontWeight={600}>
                        {formatPrice(property)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{property.city} - {property.state}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {new Date(property.created_at).toLocaleDateString('pt-BR')}
                      </Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<PhotoIcon />}
                        onClick={() => openImageModal(property)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Ver anúncio
                      </Button>
                    </TableCell>
                    <TableCell align='center'>
                      <Box display='flex' gap={1} justifyContent='center'>
                        <Button
                          variant='contained'
                          color='success'
                          size='small'
                          startIcon={processing[property.id] ? <CircularProgress size={14} color='inherit' /> : <ApproveIcon />}
                          onClick={() => handleAction(property.id, 'approved')}
                          disabled={!!processing[property.id]}
                        >
                          Aprovar
                        </Button>
                        <Button
                          variant='outlined'
                          color='error'
                          size='small'
                          startIcon={processing[property.id] ? <CircularProgress size={14} color='inherit' /> : <RejectIcon />}
                          onClick={() => handleAction(property.id, 'rejected')}
                          disabled={!!processing[property.id]}
                        >
                          Reprovar
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal de imagens */}
      <Dialog
        open={imageModal.open}
        onClose={closeImageModal}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box>
              <Typography variant='h6' fontWeight={700}>
                {imageModal.property?.title}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {currentImages.length} foto{currentImages.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <IconButton onClick={closeImageModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Foto principal em destaque */}
          {currentImg && (
            <Box sx={{ position: 'relative', bgcolor: 'black' }}>
              <Box
                component='img'
                src={getImageUrl(currentImg)}
                alt={`Foto ${imageModal.currentIndex + 1}`}
                sx={{
                  width: '100%',
                  maxHeight: 420,
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
              {currentImages.length > 1 && (
                <>
                  <IconButton
                    onClick={() => setImageModal((s) => ({ ...s, currentIndex: Math.max(0, s.currentIndex - 1) }))}
                    disabled={imageModal.currentIndex === 0}
                    sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' } }}
                  >
                    <PrevIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setImageModal((s) => ({ ...s, currentIndex: Math.min(currentImages.length - 1, s.currentIndex + 1) }))}
                    disabled={imageModal.currentIndex === currentImages.length - 1}
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' } }}
                  >
                    <NextIcon />
                  </IconButton>
                  <Chip
                    label={`${imageModal.currentIndex + 1} / ${currentImages.length}`}
                    size='small'
                    sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white' }}
                  />
                </>
              )}
            </Box>
          )}

          {/* Miniaturas */}
          {currentImages.length > 1 && (
            <Box sx={{ p: 1.5 }}>
              <ImageList cols={Math.min(currentImages.length, 8)} gap={6} sx={{ m: 0 }}>
                {currentImages.map((img, idx) => (
                  <ImageListItem
                    key={img.id || idx}
                    onClick={() => setImageModal((s) => ({ ...s, currentIndex: idx }))}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      overflow: 'hidden',
                      outline: idx === imageModal.currentIndex ? '2px solid' : 'none',
                      outlineColor: 'primary.main',
                      opacity: idx === imageModal.currentIndex ? 1 : 0.65,
                      transition: 'opacity 0.15s',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`Miniatura ${idx + 1}`}
                      style={{ width: '100%', height: 64, objectFit: 'cover' }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}

          {/* Detalhes e Descrição */}
          {imageModal.property && (
            <Box sx={{ p: 2.5 }}>
              <Divider sx={{ mb: 2 }} />

              {/* Características rápidas */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <AreaIcon fontSize='small' color='action' />
                    <Typography variant='body2'>
                      <strong>{imageModal.property.area} m²</strong>
                    </Typography>
                  </Box>
                </Grid>
                {imageModal.property.bedrooms > 0 && (
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box display='flex' alignItems='center' gap={0.5}>
                      <BedIcon fontSize='small' color='action' />
                      <Typography variant='body2'>
                        <strong>{imageModal.property.bedrooms}</strong> quarto{imageModal.property.bedrooms !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {imageModal.property.bathrooms > 0 && (
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box display='flex' alignItems='center' gap={0.5}>
                      <BathIcon fontSize='small' color='action' />
                      <Typography variant='body2'>
                        <strong>{imageModal.property.bathrooms}</strong> banheiro{imageModal.property.bathrooms !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {imageModal.property.garage_spaces > 0 && (
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box display='flex' alignItems='center' gap={0.5}>
                      <GarageIcon fontSize='small' color='action' />
                      <Typography variant='body2'>
                        <strong>{imageModal.property.garage_spaces}</strong> vaga{imageModal.property.garage_spaces !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Descrição */}
              <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                Descrição
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}
              >
                {imageModal.property.description || 'Sem descrição.'}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant='outlined' color='error' startIcon={<RejectIcon />}
            onClick={() => handleAction(imageModal.property.id, 'rejected')}
            disabled={!!processing[imageModal.property?.id]}
          >
            Reprovar
          </Button>
          <Button variant='contained' color='success' startIcon={<ApproveIcon />}
            onClick={() => handleAction(imageModal.property.id, 'approved')}
            disabled={!!processing[imageModal.property?.id]}
          >
            Aprovar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AdminApproval
